import { useNavigate, useParams } from 'react-router-dom';
import { CompositeCapture } from '@/components/deai-photobooth-composite-capture';
import { PhotoPreviewModal } from '@/components/deai-photobooth-photo-preview-modal';
import { useAppStore } from  "@/hooks/use-app-store"
import { useEffect, useRef, useState, useCallback } from 'react';
import { DEAIVisionWebClient } from '@akavirtual/deai-web-core';
import { cn } from '@/lib/utils';

export const PhotoboothPage = () => {
  const navigate = useNavigate();
  const { source } = useParams<{ source?: string }>();
  const deaiSceneRef = useRef<DEAIVisionWebClient>(null);
  const avatarContainerRef = useRef<HTMLDivElement>(null);
  const compositeCaptureRef = useRef<{ capturePhoto: () => Promise<void> }>(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { 
    sub, 
    unsub, 
    triggerAvatarAnimator, 
    connectWebSocket, 
    isConnected,
    photoboothNavigation,
    clearPhotoboothNavigationData
  } = useAppStore();

  // Determine overlay image based on navigation data
  const getOverlayImage = () => {
    if (photoboothNavigation?.overlayImage) {
      return photoboothNavigation.overlayImage;
    }
    /*
    // Default overlays based on source experience
    if (source === 'fortune-telling' || photoboothNavigation?.sourceExperience === 'fortune-telling') {
      return 'img/PhotoboothOverlay.png';
    }
    if (source === 'potion-craft' || photoboothNavigation?.sourceExperience === 'potion-craft') {
      return 'img/PhotoboothOverlay.png';
    }
      */
    
    // Default overlay
    return '/img/PhotoboothOverlay.png';
  };

  // Clean up navigation data when component unmounts
  useEffect(() => {
    return () => {
      clearPhotoboothNavigationData();
    };
  }, [clearPhotoboothNavigationData]);

  const handlePhotoCapture = async (imageBlob: Blob) => {
    setCapturedPhoto(imageBlob);
    setShowPreviewModal(true);
    console.log('Photo captured:', imageBlob.size, 'bytes');
    
    // Automatically upload the photo
    await handleUploadPhoto(imageBlob);
  };

  const handleRetakePhoto = () => {
    setCapturedPhoto(null);
    setShowPreviewModal(false);
    setUploadedUrl(null);
  };

  const handleCloseModal = () => {
    setShowPreviewModal(false);
  };

  const handleCaptureAvatar = useCallback((): Promise<HTMLCanvasElement | null> => {
    return new Promise((resolve) => {
      if (deaiSceneRef.current?.captureFrame) {
        const avatarFrame = deaiSceneRef.current.captureFrame();
        resolve(avatarFrame);
      } else {
        console.warn('DEAIVisionWebClient captureFrame method not available');
        resolve(null);
      }
    });
  }, []);

  const startCountdown = useCallback(() => {
    if (isCountingDown) return;
    
    setIsCountingDown(true);
    setCountdown(3);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setIsCountingDown(false);
          
          // Trigger photo capture after countdown
          setTimeout(async () => {
            await compositeCaptureRef.current?.capturePhoto();
          }, 100);
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    if (isModelLoaded) {
      triggerAvatarAnimator(0, 'StartPoses');
    }
  }, [isCountingDown, isModelLoaded, triggerAvatarAnimator]);

  const handleUploadPhoto = async (blob: Blob): Promise<string | null> => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', blob, 'photo.jpg');

      const response = await fetch('/photo/upload-photo', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setUploadedUrl(result.url);
        return result.url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('message', payload);
  }, []);

  const handleAudioMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('audio', payload);
  }, []);

  const handleSystemMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('system_event', payload);
  }, []);

  const handleVisionMessage = useCallback((payload?: object ) => {
    const p = payload as { cmd: string; payload?: object };
    if (p && p.cmd) {
      deaiSceneRef.current?.dispatchMessage(p.cmd, p.payload);
    }
  }, []);

  // Effect for initial connection
  useEffect(() => {
    if(!isConnected) {
      connectWebSocket(); 
    }
  }, [connectWebSocket, isConnected]);

  // Effect to trigger avatar animation when model loads
  useEffect(() => {
    if (isModelLoaded) {
      triggerAvatarAnimator(0, 'StartIdle');
    }
  }, [isModelLoaded, triggerAvatarAnimator]);

  useEffect(() => {
    sub('message', handleMessage);
    sub('system_event', handleSystemMessage);
    sub('audio', handleAudioMessage);
    sub('vision', handleVisionMessage);
    return () => {
      unsub('message', handleMessage);
      unsub('system_event', handleSystemMessage);
      unsub('audio', handleAudioMessage);
      unsub('vision', handleVisionMessage);
    };
  }, [sub, unsub, handleMessage, handleAudioMessage, handleSystemMessage, handleVisionMessage]);

  return (
    <div className="h-screen w-screen relative overflow-hidden flex items-center justify-center">
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed bg-dark-black z-0"
        style={{ backgroundImage: 'url(/img/SHRINE_BG.png)' }}
      />

      {/* Back button */}
      <div className="absolute top-8 left-8 z-50">
        <button
          onClick={() => navigate('/')}
          className="bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full p-4 transition-colors"
        >
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Square preview and capture button container */}
      <div className="flex flex-col items-center gap-8">
        {/* Square preview container - centered */}
        <div className="relative w-[90dvw] h-[90dvw] bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
          {/* Composite webcam and avatar feed - contained in square */}
          <div className="absolute inset-0 z-10">
            <CompositeCapture 
              ref={compositeCaptureRef}
              onCapture={handlePhotoCapture}
              avatarContainerRef={avatarContainerRef}
              onCaptureAvatar={handleCaptureAvatar}
              className="w-full h-full"
            />
          </div>

          {/* 3D Avatar positioned to fill the square preview */}
          <div className="absolute inset-0 z-20" ref={avatarContainerRef}>
            <div className={cn("pointer-events-none relative w-full h-full")}>
              <img
                src="img/loading-placeholder.png"
                alt="Avatar loading placeholder"
                className={cn(
                  "absolute bottom-4 right-4 max-w-[100px]",
                  "transition-opacity duration-[1300ms] ease-out",
                  isModelLoaded ? "opacity-0" : "opacity-100"
                )}
              />
              <DEAIVisionWebClient
                className={cn(
                  "w-full h-full transition-opacity duration-[1300ms] ease-out",
                  isModelLoaded ? "opacity-100" : "opacity-0"
                )}
                vrmUrl={"/avatar/Miko.vrm"}
                vrmaUrl={"/animations/Shiryu/Idle/Shiryu Idle 2024-10-23 Take_000_FBX_1.vrma"}
                vrmaLibRoot={"/animations/Noura/"}
                vrmaLibConfig={"AnimationConfig.json"}
                animatorLibRoot={"/animators/Noura/"}
                animatorLibConfig={"AnimatorConfig.json"}
                debugDragNDrop={true}
                initCameraPosition={{ 
                  x: 0.3, y: 1.4, z: -1.8  // Matching working config from DEAI3DScene
                }}
                onInitialized={() => {
                  setIsModelLoaded(true);
                }}
                ref={deaiSceneRef}
              />
            </div>
          </div>

          {/* Photo overlay frame - above avatar but below countdown */}
          <div className="absolute inset-0 z-25 pointer-events-none">
            <img
              src={getOverlayImage()}
              alt="Photo frame overlay"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Countdown overlay - positioned above everything */}
          {isCountingDown && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30 pointer-events-none">
              <div className="text-white text-9xl font-bold animate-pulse">
                {countdown}
              </div>
            </div>
          )}
        </div>

        {/* Capture button - positioned below the square */}
        <button
          onClick={startCountdown}
          disabled={isCountingDown}
          className="bg-white rounded-full w-24 h-24 shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed z-20"
        >
          <div className="w-20 h-20 rounded-full bg-red-500 mx-auto my-2"></div>
        </button>
      </div>

      {/* Instructions overlay - positioned outside the square */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-40">
        <div className="bg-black/50 backdrop-blur-md rounded-lg p-6 text-white text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">Photo Booth Time!</h2>
          <p className="text-sm opacity-80">
            Position yourself in front of the camera and click the capture button when ready.
          </p>
        </div>
      </div>

      {/* Photo Preview Modal */}
      <PhotoPreviewModal
        isOpen={showPreviewModal}
        photoBlob={capturedPhoto}
        onRetake={handleRetakePhoto}
        onDone={() => navigate('/')}
        uploadedUrl={uploadedUrl}
        isUploading={isUploading}
      />
    </div>
  );
};